import permissionRoles from "./permissionRoles";

const ROLE_HIERARCHY = [
    "user",
    "admin",
    "owner",
    "pawn",
    "knight",
    "rook",
    "queen",
    "king"
];

type UserOrRole = string | { role: string; };

const checkPermission = (userOrRole: UserOrRole | undefined | null, allowedRoles: string[]) => {
    if (!userOrRole) {
        return false;
    }

    const role = typeof userOrRole === "string" ? userOrRole : userOrRole.role;

    return allowedRoles.includes(role);
};

const havePermission = (allowedRoles: string[]) =>
    (userOrRole?: UserOrRole | null) => checkPermission(userOrRole, allowedRoles);

const permissions = {
    // orEqual ? lUser >= rUser : lUser > rUser
    isSuperior(lUser: { role: string; }, rUser: { role: string; }, orEqual: boolean): boolean {
        const lRoleIndex = ROLE_HIERARCHY.indexOf(lUser.role);
        const rRoleIndex = ROLE_HIERARCHY.indexOf(rUser.role);

        return (orEqual && lRoleIndex === rRoleIndex) || lRoleIndex > rRoleIndex;
    },
    canTransferOwnership: havePermission(permissionRoles.canTransferOwnership),
    canReceiveOwnership: havePermission(permissionRoles.canReceiveOwnership),
    canLeaveProject: havePermission(permissionRoles.canLeaveProject),
    canCreateProject: havePermission(permissionRoles.canCreateProject),
    canUpdateProject: havePermission(permissionRoles.canUpdateProject),
    canCreateProjectSection: havePermission(permissionRoles.canCreateProjectSection),
    canChangeProjectAccesibility: havePermission(permissionRoles.canChangeProjectAccesibility),
    canChangeProjectStatus: havePermission(permissionRoles.canChangeProjectStatus),
    canDeleteProject: havePermission(permissionRoles.canDeleteProject),
    canCreateStyleguide: havePermission(permissionRoles.canCreateStyleguide),
    canDeleteStyleguide: havePermission(permissionRoles.canDeleteStyleguide),
    canLeaveStyleguide: havePermission(permissionRoles.canLeaveStyleguide),
    canUpdateStyleguide: havePermission(permissionRoles.canUpdateStyleguide),
    canChangeStyleguideStatus: havePermission(permissionRoles.canChangeStyleguideStatus),
    canManageScreens: havePermission(permissionRoles.canManageScreens),
    canMoveScreens: havePermission(permissionRoles.canMoveScreens),
    canRemoveVersions: havePermission(permissionRoles.canRemoveVersions),
    canManageColors: havePermission(permissionRoles.canManageColors),
    canManageTextStyles: havePermission(permissionRoles.canManageTextStyles),
    canManageSpacings: havePermission(permissionRoles.canManageSpacings),
    canRemoveNotes: havePermission(permissionRoles.canRemoveNotes),
    canRemoveExtensions: havePermission(permissionRoles.canRemoveExtensions),
    canManageRemSettings: havePermission(permissionRoles.canManageRemSettings),
    canManageWorkflowStatuses: havePermission(permissionRoles.canManageWorkflowStatuses),
    canMoveResources: havePermission(permissionRoles.canMoveResources),
    canManageWebhooks: havePermission(permissionRoles.canManageWebhooks),
    canChangePlan: havePermission(permissionRoles.canChangePlan),
    canCopyBarrel: havePermission(permissionRoles.canCopyBarrel),
    canHighlightComponents: havePermission(permissionRoles.canHighlightComponents),
    canDisplayGridView: havePermission(permissionRoles.canDisplayGridView),
    canManageStorybookIntegration: havePermission(permissionRoles.canManageStorybookIntegration),
    canSeeOthersEmail: havePermission(permissionRoles.canSeeOthersEmail),
    canCreateAnnotation: havePermission(permissionRoles.canCreateAnnotation),
    canUpdateAnnotation: havePermission(permissionRoles.canUpdateAnnotation),
    canRemoveAnnotation: havePermission(permissionRoles.canRemoveAnnotations),
    canRequestActivityLogs: havePermission(permissionRoles.canRequestActivityLogs),
    canManageComponents: havePermission(permissionRoles.canManageComponents),
    canManageApprovals: havePermission(permissionRoles.canManageApprovals),
    canCreateApprovals: havePermission(permissionRoles.canCreateApprovals),
    canDisplayApprovalsReminderPopup: havePermission(permissionRoles.canDisplayApprovalsReminderPopup),
    canChangeCommitcolor: havePermission(permissionRoles.canChangeCommitColor),
    canSyncStyleguideLibraryWithFigma: havePermission(permissionRoles.canSyncStyleguideLibraryWithFigma),
    canCustomizeNoteTypes: havePermission(permissionRoles.canCustomizeNoteTypes)
};

export default permissions;
